// Google Analytics (GA4) integration for the VerifiedID app.
//
// One React codebase is deployed as several apps (ADMIN / HR / CLIENT / QA /
// EXECUTIVE / CANDIDATE) across two environments (dev / prod). Each build reads
// its identity from the matching .env.<app>.<env> file:
//   REACT_APP_GA_MEASUREMENT_ID  GA4 measurement id (empty => analytics disabled)
//   REACT_APP_SELECTED_ENV       app: ADMIN / HR / CLIENT / QA / EXECUTIVE / CANDIDATE
//   REACT_APP_DEPLOY_ENV         environment: dev / prod
//
// Every prod *.verifiedid.co subdomain shares the same measurement id, so GA4
// stitches a visitor's session across the apps AND the marketing site
// automatically (one _ga cookie on the root domain). That is what makes
// cross-app funnels possible, e.g. pre-check -> marketing site -> book a consult.

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID
const APP = process.env.REACT_APP_SELECTED_ENV || 'UNKNOWN'
const ENVIRONMENT = process.env.REACT_APP_DEPLOY_ENV || 'unknown'

// Query params that carry secrets/PII and must never reach Google. Our email
// links land on e.g. /pre-check-view?key=<secret token>, and GA otherwise sends
// the full URL on every page_view.
const SENSITIVE_QUERY_KEYS = ['key', 'token', 'access_token', 'code', 'secret', 'password', 'email']

// Identifiers that appear as path segments, e.g. /pre-check/<uuid>. They must be
// collapsed to a placeholder before send, for two reasons:
//   1. Cardinality — a raw id makes every case its own row in "Pages and screens".
//      GA4 caps dimension cardinality and dumps the overflow into "(other)", which
//      would wreck the page reports rather than enrich them.
//   2. Secrets — /candidate-consumer-report/<reportKey> carries a live access key
//      for a candidate's report, exactly like the ?key= tokens redacted above.
// Matched conservatively so real page names survive: the hyphenated route names
// ("manage-individual-background-check-items") and short slugs ("US", a check key)
// are left alone, while opaque ids/tokens are replaced.
const ID_SEGMENT_PATTERNS = [
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, // uuid (our IdService format)
    /^[0-9a-f]{16,}$/i,      // hex token, e.g. crypto.randomBytes(...).toString('hex')
    /^[A-Za-z0-9_]{20,}$/,   // any other long opaque token (no hyphen => never a route name)
    /^\d+$/,                 // bare numeric id
]

const isIdSegment = (segment) => ID_SEGMENT_PATTERNS.some((re) => re.test(segment))

// /pre-check/6f1c...-...  ->  /pre-check/:id
export const normalizePath = (pathname) => {
    if (!pathname) return pathname
    return pathname
        .split('/')
        .map((segment) => (segment && isIdSegment(segment) ? ':id' : segment))
        .join('/')
}

let initialized = false

export const isAnalyticsEnabled = () => Boolean(MEASUREMENT_ID)

// Replace secret query values with REDACTED (keep the key name so paths stay
// readable in reports).
const redactSearch = (search) => {
    if (!search) return ''
    const params = new URLSearchParams(search)
    SENSITIVE_QUERY_KEYS.forEach((k) => {
        if (params.has(k)) params.set(k, 'REDACTED')
    })
    const out = params.toString()
    return out ? `?${out}` : ''
}

// Safe page path (normalized pathname + redacted query) from a location-like object.
export const buildPagePath = (location) => {
    const pathname = location?.pathname ?? window.location.pathname
    const search = location?.search ?? window.location.search
    return `${normalizePath(pathname)}${redactSearch(search)}`
}

export const initAnalytics = () => {
    if (initialized || !MEASUREMENT_ID || typeof window === 'undefined') return
    initialized = true

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`
    document.head.appendChild(script)

    window.dataLayer = window.dataLayer || []
    function gtag() { window.dataLayer.push(arguments) }
    window.gtag = gtag

    gtag('js', new Date())
    // send_page_view:false -> the router listener owns every page_view, so the
    // initial hit is never double counted against our manual page_view.
    gtag('config', MEASUREMENT_ID, { send_page_view: false })
    // User-scoped: "which app does this person mainly use". Deliberately NOT the
    // thing reports break pages down by — see eventScope() below.
    gtag('set', 'user_properties', { app: APP, environment: ENVIRONMENT })
}

// Every prod app is a different subdomain of verifiedid.co sharing ONE measurement
// id, so "/" from admin.app, client.app and the marketing site all land on the same
// "Page path" row. The user_properties above cannot separate them: they are
// USER-scoped, so GA4 reports them at their latest value per user. The moment a
// visitor crosses from client.app.verifiedid.co to verifiedid.co — the exact
// cross-app journey this property exists to measure — their earlier CLIENT hits get
// re-attributed to WEBSITE. Stamping app/environment on each event as well makes
// every hit carry the app that actually produced it, immune to later overwrites.
const eventScope = () => ({ app: APP, environment: ENVIRONMENT })

// Send a page_view manually. Used by the router listener and for "virtual"
// pageviews inside multi-step pages that share a single route (pass a synthetic
// path, e.g. sendPageView({ path: '/pre-check-form/step-2-documents' })).
export const sendPageView = ({ path, title } = {}) => {
    if (!window.gtag) return
    // page_location keeps the real origin, so GA4's built-in Hostname dimension
    // still resolves the subdomain — only the path is normalized.
    const pagePath = path ? normalizePath(path) : buildPagePath()
    window.gtag('event', 'page_view', {
        ...eventScope(),
        page_path: pagePath,
        page_location: `${window.location.origin}${pagePath}`,
        page_title: title || document.title,
    })
}

// Track an arbitrary interaction / funnel step, e.g.
//   trackEvent('book_consultant_click', { source: 'trucheck_card' })
export const trackEvent = (eventName, params = {}) => {
    if (!window.gtag) return
    window.gtag('event', eventName, { ...eventScope(), ...params })
}
